import type { ModeConfig } from "../state/modeConfig";
import { useTranslation } from "../i18n/useTranslation";

interface ModeCardProps {
  config: ModeConfig;
  isUnlocked: boolean;
  highScore: number;
  difficulty: number;
  onClick: () => void;
  /** 1-based card index (01..07) shown as a mono number tag. */
  index: number;
}

/** Single row in the main menu — one card per mode. */
export function ModeCard({
  config,
  isUnlocked,
  highScore,
  difficulty,
  onClick,
  index,
}: ModeCardProps) {
  const { t } = useTranslation();
  const indexLabel = String(index).padStart(2, "0");

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isUnlocked}
      className={[
        "w-full text-left rounded-2xl border transition-all duration-150",
        "flex items-center gap-4 px-5 py-4",
        isUnlocked
          ? "bg-bg-2 border-[var(--color-line)] hover:border-[var(--color-line-strong)] hover:-translate-y-0.5 cursor-pointer"
          : "bg-bg-2/60 border-[var(--color-line)] opacity-60 cursor-not-allowed",
      ].join(" ")}
    >
      <span
        aria-hidden
        className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-mono text-xs"
        style={{
          background: isUnlocked
            ? `color-mix(in oklab, ${config.iconColor} 18%, transparent)`
            : "rgba(255,255,255,0.04)",
          color: isUnlocked ? config.iconColor : "var(--color-fg-3)",
          border: `1px solid ${isUnlocked ? config.iconColor : "var(--color-line)"}`,
        }}
      >
        {indexLabel}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-base font-medium tracking-[-0.005em] text-fg-0">
          {t(`mode.${config.modeId}.name`)}
        </span>
        <span className="block text-xs font-mono text-fg-3 mt-0.5">
          {isUnlocked
            ? t("menu.highDiff", { high: highScore, diff: difficulty })
            : t("menu.unlocksAt", { level: config.unlockLevel })}
        </span>
      </span>

      <span
        aria-hidden
        className={isUnlocked ? "text-fg-2 text-lg" : "text-fg-3 text-base"}
      >
        {isUnlocked ? "→" : "🔒"}
      </span>
    </button>
  );
}
